import { routeCatalog } from "./catalog/route-catalog.ts";
import { routeParameter } from "./route-parameter.ts";
import type { Destination } from "./routes.types.ts";

type RouteValues = Record<string, string | readonly string[] | undefined>;

function encodeWildcard({
  value,
  parameter: { key, optional, suffix },
}: {
  readonly value: string | readonly string[];
  readonly parameter: NonNullable<ReturnType<typeof routeParameter>>;
}): string[] {
  if (!Array.isArray(value) || (!optional && value.length === 0))
    throw new Error(`Route parameter ${key} requires path segments`);

  return value.map(
    (part, index) =>
      `${encodeURIComponent(part)}${index === value.length - 1 ? suffix : ""}`,
  );
}

function encodeRouteSegment({
  segment,
  values,
  policyVersion,
}: {
  readonly segment: string;
  readonly values: Readonly<RouteValues>;
  readonly policyVersion: number | null | undefined;
}): string[] {
  const parameter = routeParameter(segment);

  if (!parameter) return [segment];

  const { optional, key, prefix, suffix } = parameter;
  const value = values[key];

  if (value === undefined) {
    if (optional) return [];

    throw new Error(`Missing route parameter: ${key}`);
  }

  if (parameter.wildcard) return encodeWildcard({ value, parameter });

  if (typeof value !== "string")
    throw new Error(`Route parameter ${key} requires one segment`);

  let encoded = encodeURIComponent(value);

  if (key === "policyName" && policyVersion != null)
    encoded += `:v${policyVersion}`;

  return [`${prefix}${encoded}${suffix}`];
}

function destinationSearch(destination: Destination): string {
  const params = new URLSearchParams(destination.search);

  if (destination.name === "admin.replay") {
    params.set("replay_uri", destination.replayUri);
    params.set("game", destination.telemetry.game ?? "");
    params.set("game_version", destination.telemetry.gameVersion ?? "");
    params.set("episode_id", destination.telemetry.episodeId);
  }

  if (
    (destination.name === "admin.replay" ||
      destination.name === "admin.episode") &&
    destination.achievement
  )
    params.set("achievement", destination.achievement);

  return params.toString();
}

function destinationPath(destination: Destination): string {
  const definition = routeCatalog[destination.name];
  const values: RouteValues = {};

  for (const [key, value] of Object.entries(destination)) {
    if (typeof value === "string" || Array.isArray(value)) values[key] = value;
  }

  const game = "game" in destination ? destination.game : undefined;

  if (game) values.gameSlug = game.slug;

  const parts: string[] = [];

  if (game?.catalog_status === "unlisted") parts.push("unlisted");

  if (game && definition.scope === "resource")
    parts.push(encodeURIComponent(game.slug));

  const policyVersion =
    destination.name === "policy" ? destination.version : undefined;

  for (const segment of definition.path.split("/").filter(Boolean)) {
    parts.push(...encodeRouteSegment({ segment, values, policyVersion }));
  }

  return `/${parts.join("/")}`;
}

export function buildHref(destination: Destination): string {
  const path = destinationPath(destination);
  const search = destinationSearch(destination);
  const hash = destination.hash ? `#${destination.hash.replace(/^#/, "")}` : "";
  return `${path}${search ? `?${search}` : ""}${hash}`;
}
